import type { Renderable } from '@adornis/functional-lit/renderable.js';
import { stateful, useEffect, useObservable, useState } from '@adornis/functional-lit/stateful.js';
import { useQuery } from '@adornis/functional-lit/use-query.js';
import { goTo } from '@adornis/router/client/open-href.js';
import { functionalLogin } from '@adornis/user-forms/function-login.js';
import { currentUser } from '@adornis/users/client/currentUser.js';
import { logout } from '@adornis/users/client/loginFunctions.js';
import { AdornisUser } from '@adornis/users/db/a-user.js';
import { passwordRegexCheck } from '@adornis/users/db/validators.js';
import { html, nothing } from 'lit';
import {
  clearMustChangePasswordFlag,
  getCurrentFieldEngineerMustChangePassword,
  sendCurrentUserPasswordResetEmail,
} from '../api/password-enforcement.js';

// TODO this entire thing should live in engine

/**
 * The rule the server enforces on a new password, checked here before we send it. `changePassword`
 * / `changePasswordWithToken` reject a weak password with a validation error, which BaseQL logs as
 * a fatal server error and alerts on — so a user's typo used to page the on-call channel. Reuses
 * engine's own regex (`@adornis/users/db/validators.js`) so the two cannot drift apart.
 */
const PASSWORD_RULE_TEXT =
  'Password must be at least 8 characters and contain an uppercase letter, a lowercase letter, a number and a special character.';

const passwordMeetsPolicy = (password: string): boolean =>
  passwordRegexCheck(password) && /[^a-zA-Z0-9]/.test(password);

/** Login/reset header: the app's bundled logo when given, else the styled "SmartTeam" wordmark. */
const brandLockup = (logoSrc?: string) =>
  logoSrc
    ? html`<img src=${logoSrc} alt="SmartTeam" class="mx-auto h-auto w-56" />`
    : html`<div class="text-4xl font-extrabold tracking-tight text-neutral">
        <span class="text-primary">Smart</span>Team
      </div>`;

type PasswordEnforcementGateParams = {
  appContent: () => Renderable;
  changePasswordPath: string;
  appType: 'admin' | 'field-engineer';
  /** Bundled SmartTeam logo URL, shown on the login / reset screens. */
  logoSrc?: string;
  /** App version (e.g. "1.0.1"), shown on the login screen when provided. */
  version?: string;
};

export const SharedPasswordResetPage = stateful(() => {
  const token = new URLSearchParams(window.location.search).get('token') ?? '';
  const user = useObservable(() => currentUser(), []);
  const [firstPassword, setFirstPassword] = useState('');
  const [secondPassword, setSecondPassword] = useState('');
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  const handleResend = async () => {
    if (!email) {
      setErrorMessage('Please enter your email address');
      return;
    }

    setLoading(true);
    setErrorMessage('');
    setSuccessMessage('');

    try {
      const sent = await sendCurrentUserPasswordResetEmail(email)();
      if (!sent) {
        setErrorMessage('Could not send reset email. Please contact your administrator.');
        return;
      }
      setSuccessMessage('A new password reset link has been sent to your email.');
    } catch (error: unknown) {
      setErrorMessage(error instanceof Error ? error.message : 'Failed to send reset email');
    } finally {
      setLoading(false);
    }
  };

  const handleChangePassword = async () => {
    if (firstPassword !== secondPassword) {
      setErrorMessage('Passwords do not match');
      return;
    }

    if (!passwordMeetsPolicy(firstPassword)) {
      setErrorMessage(PASSWORD_RULE_TEXT);
      return;
    }

    setLoading(true);
    setErrorMessage('');
    setSuccessMessage('');

    try {
      if (user?._id) {
        await AdornisUser.changePassword(user._id, firstPassword);
      } else {
        if (!token) {
          setErrorMessage('Missing reset token. Please request a new reset email.');
          return;
        }
        await AdornisUser.changePasswordWithToken(token, firstPassword, true)();
      }

      await clearMustChangePasswordFlag()();
      setSuccessMessage('Password changed successfully. You can continue now.');
      setTimeout(() => goTo('/'), 1000);
    } catch (error: unknown) {
      setErrorMessage(error instanceof Error ? error.message : 'Failed to change password');
    } finally {
      setLoading(false);
    }
  };

  if (!token && !user?._id) {
    return html`
      <section class="h-full bg-base-100 p-4 md:p-6 flex items-center justify-center">
        <div class="card w-full max-w-md bg-base-200 border border-base-300 shadow-sm">
          <div class="card-body gap-4">
            <h1 class="card-title text-xl">Password Change Required</h1>
            <p class="text-base-content/70">Your account is locked until you change your password.</p>
            <p class="text-sm text-base-content/70">We can send a fresh reset link to your email.</p>
            ${errorMessage ? html`<p class="text-error text-sm">${errorMessage}</p>` : nothing}
            ${successMessage ? html`<p class="text-success text-sm">${successMessage}</p>` : nothing}
            <div class="form-control">
              <label class="label py-0">
                <span class="label-text">Email address</span>
              </label>
              <input
                type="email"
                class="input input-bordered"
                placeholder="Enter your email address"
                .value=${email}
                @input=${(event: Event) => setEmail((event.target as HTMLInputElement).value)}
              />
            </div>
            <div class="card-actions justify-end">
              <button class="btn btn-primary" ?disabled=${loading} @click=${handleResend}>
                ${loading ? html`<span class="loading loading-spinner loading-sm"></span>` : nothing} Send new reset
                email
              </button>
            </div>
          </div>
        </div>
      </section>
    `;
  }

  return html`
    <section class="h-full bg-base-100 p-4 md:p-6 flex items-center justify-center">
      <div class="card w-full max-w-md bg-base-200 border border-base-300 shadow-sm">
        <div class="card-body gap-4">
          <h1 class="card-title text-xl">Change Password</h1>
          ${errorMessage ? html`<p class="text-error text-sm">${errorMessage}</p>` : nothing}
          ${successMessage ? html`<p class="text-success text-sm">${successMessage}</p>` : nothing}
          <div class="form-control">
            <label class="label py-0">
              <span class="label-text">New password</span>
            </label>
            <input
              type="password"
              class="input input-bordered w-full"
              .value=${firstPassword}
              @input=${(event: Event) => setFirstPassword((event.target as HTMLInputElement).value)}
            />
          </div>
          <p class="text-xs text-base-content/60">${PASSWORD_RULE_TEXT}</p>
          <div class="form-control">
            <label class="label py-0">
              <span class="label-text">Confirm password</span>
            </label>
            <input
              type="password"
              class="input input-bordered w-full"
              .value=${secondPassword}
              @input=${(event: Event) => setSecondPassword((event.target as HTMLInputElement).value)}
            />
          </div>
          <div class="card-actions justify-between items-center gap-4">
            <button class="btn btn-ghost" @click=${() => logout()}>Logout</button>
            <button class="btn btn-primary" ?disabled=${loading || !!successMessage} @click=${handleChangePassword}>
              ${loading ? html`<span class="loading loading-spinner loading-sm"></span>` : nothing} Update password
            </button>
          </div>
        </div>
      </div>
    </section>
  `;
});

export const PasswordEnforcementGate = stateful(
  ({ appContent, changePasswordPath, appType, logoSrc, version }: PasswordEnforcementGateParams) => {
    const { value: user } = useQuery(() => currentUser(), []);
    const { value: mustChangePassword, loading } = useQuery(() => getCurrentFieldEngineerMustChangePassword()(), []);
    const isOnChangePasswordPath = window.location.pathname === changePasswordPath;
    const token = new URLSearchParams(window.location.search).get('token') ?? '';

    useEffect(() => {
      if (user?._id && mustChangePassword && !isOnChangePasswordPath) goTo(changePasswordPath);
    }, [user?._id, mustChangePassword, isOnChangePasswordPath, changePasswordPath]);

    // Always allow access to password reset page when a token exists in URL
    if (isOnChangePasswordPath && (token || user?._id)) {
      return html`<section class="h-full bg-base-100 p-4 md:p-6">${SharedPasswordResetPage({})}</section>`;
    }

    // Show loading while user is being determined
    if (loading && user?._id) {
      return html`<section class="h-full bg-base-100 flex items-center justify-center">
        <span class="loading loading-spinner loading-lg"></span>
      </section>`;
    }

    // Show login page if no user
    if (!user?._id) {
      return SharedLoginPage({ appType, logoSrc, version });
    }

    // Show main app content
    return appContent();
  },
);

type SharedLoginPageParams = {
  appType: 'admin' | 'field-engineer';
  subtitle?: string;
  description?: string;
  logoSrc?: string;
  /** App version (e.g. "1.0.1"), shown discreetly below the login form when provided. */
  version?: string;
};

export const SharedPasswordResetRequest = stateful(({ logoSrc }: { logoSrc?: string }) => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  const handleResend = async () => {
    if (!email) {
      setErrorMessage('Please enter your email address');
      return;
    }

    setLoading(true);
    setErrorMessage('');
    setSuccessMessage('');

    try {
      const sent = await sendCurrentUserPasswordResetEmail(email)();
      if (!sent) {
        setErrorMessage('Could not send reset email. Please contact your administrator.');
        return;
      }
      setSuccessMessage('A new password reset link has been sent to your email.');
    } catch (error: unknown) {
      setErrorMessage(error instanceof Error ? error.message : 'Failed to send reset email');
    } finally {
      setLoading(false);
    }
  };

  return html`
    <div class="card w-full max-w-md bg-base-100 shadow-2xl border border-primary/20">
      <div class="card-body p-8">
        <div class="text-center mb-8">
          ${brandLockup(logoSrc)}
          <span class="text-base-content/60 font-normal text-xl">Field Engineer App</span>
          <p class="text-sm text-base-content/60 mt-2">Request a password reset link</p>
        </div>
        <div class="text-center mb-6">
          <p class="text-base-content/70">We can send a fresh reset link to your email.</p>
        </div>
        ${errorMessage ? html`<p class="text-error text-sm mb-4">${errorMessage}</p>` : nothing}
        ${successMessage ? html`<p class="text-success text-sm mb-4">${successMessage}</p>` : nothing}
        <div class="form-control">
          <label class="label py-0">
            <span class="label-text">Email address</span>
          </label>
          <input
            type="email"
            class="input input-bordered"
            placeholder="Enter your email address"
            .value=${email}
            @input=${(event: Event) => setEmail((event.target as HTMLInputElement).value)}
          />
        </div>
        <div class="card-actions justify-center mt-4">
          <button class="btn btn-primary" ?disabled=${loading} @click=${handleResend}>
            ${loading ? html`<span class="loading loading-spinner loading-sm"></span>` : nothing} Send reset email
          </button>
        </div>
        <div class="text-center mt-6">
          <a href="/login" class="link link-primary text-sm">← Back to login</a>
        </div>
      </div>
    </div>
  `;
});

export const SharedLoginPage = stateful(({ appType, subtitle, description, logoSrc, version }: SharedLoginPageParams) => {
  const showReset = window.location.pathname === '/reset-password';

  if (showReset) {
    return html`
      <div
        class="min-h-screen bg-gradient-to-br from-base-100 via-primary/5 to-base-200 flex items-center justify-center p-4"
      >
        ${SharedPasswordResetRequest({ logoSrc })}
      </div>
    `;
  }

  const isFieldEngineer = appType === 'field-engineer';
  const defaultSubtitle = isFieldEngineer ? 'Field Engineer App' : subtitle || 'User Management';
  const defaultDescription = isFieldEngineer
    ? 'Sign in to access your courses, tickets and data.'
    : description || 'Sign in to access the dashboard';

  return html`
    <div
      class="min-h-screen bg-gradient-to-br from-base-100 via-primary/5 to-base-200 flex items-center justify-center p-4"
    >
      <div class="card w-full max-w-md bg-base-100 shadow-2xl border border-primary/20">
        <div class="card-body p-8">
          <div class="text-center mb-8">
            <div class="mb-4">${brandLockup(logoSrc)}</div>
            ${!isFieldEngineer
              ? html`<h1 class="text-xl font-bold text-base-content">${defaultSubtitle}</h1>`
              : html`<span class="text-base-content/60 font-normal text-xl">${defaultSubtitle}</span>`}
            <p class="text-sm text-base-content/60 mt-2">${defaultDescription}</p>
          </div>
          <div class="flex flex-col items-center justify-center">
            ${functionalLogin({
              placeholderUsername: 'email',
              placeholderPassword: 'password',
              // TODO because the redirect to /reset-password is only working if I reload the page. Can't get resolved today.
              onLogin: () => window.location.reload(),
            })}
          </div>
          ${isFieldEngineer
            ? html`
                <div class="text-center mt-6">
                  <a href="/reset-password" class="link link-primary text-sm">Forgot your password?</a>
                </div>
              `
            : nothing}
          ${version ? html`<p class="text-center text-xs text-base-content/40 mt-6">Version ${version}</p>` : nothing}
        </div>
      </div>
    </div>
  `;
});
